// Server state: TanStack Query with stale-while-revalidate and a localStorage persister, so a
// reopened app paints the cached catalogue instantly (spec §8) and refreshes in the background.
import type {
  CreatePaymentResponse,
  Lang,
  Me,
  PaymentStatusResponse,
  PlansResponse,
  StoriesResponse,
  UpdateMeBody,
} from '@fonus/shared';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { APP_VERSION } from '../env.ts';
import { api, ApiRequestError } from './client.ts';

export const keys = {
  me: ['me'] as const,
  stories: (lang: Lang) => ['stories', lang] as const,
  plans: ['plans'] as const,
  payment: (id: string) => ['payment', id] as const,
};

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 7 * 24 * 60 * 60_000,
        retry: (count, err) =>
          count < 2 && !(err instanceof ApiRequestError && err.status >= 400 && err.status < 500),
        refetchOnWindowFocus: true,
        networkMode: 'offlineFirst',
      },
    },
  });
}

export const persister = createAsyncStoragePersister({
  storage: (() => {
    try {
      return window.localStorage;
    } catch {
      return undefined;
    }
  })(),
  key: 'fonus-ertak-cache',
  throttleTime: 1000,
});

export const persistOptions = {
  persister,
  maxAge: 7 * 24 * 60 * 60_000,
  // A new release discards the old cache shape.
  buster: APP_VERSION,
  dehydrateOptions: {
    shouldDehydrateQuery: (q: { queryKey: readonly unknown[]; state: { status: string } }) =>
      q.state.status === 'success' && ['me', 'stories', 'plans'].includes(String(q.queryKey[0])),
  },
};

export function useMe(enabled: boolean) {
  return useQuery({ queryKey: keys.me, queryFn: () => api<Me>('/me'), enabled });
}

export function useStories(lang: Lang, enabled: boolean) {
  return useQuery({
    queryKey: keys.stories(lang),
    queryFn: () => api<StoriesResponse>(`/stories?lang=${lang}`),
    enabled,
  });
}

export function usePlans(enabled = true) {
  return useQuery({
    queryKey: keys.plans,
    queryFn: () => api<PlansResponse>('/plans'),
    staleTime: 10 * 60_000,
    enabled,
  });
}

/** Optimistic profile update (language, reminders, audio quality). */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateMeBody) => api<Me>('/me', { method: 'PATCH', body }),
    onMutate: async (body) => {
      await qc.cancelQueries({ queryKey: keys.me });
      const previous = qc.getQueryData<Me>(keys.me);
      if (previous) qc.setQueryData<Me>(keys.me, { ...previous, ...body });
      return { previous };
    },
    onError: (_err, _body, ctx) => {
      if (ctx?.previous) qc.setQueryData(keys.me, ctx.previous);
    },
    onSuccess: (me) => {
      qc.setQueryData(keys.me, me);
    },
  });
}

export function useCreatePayment() {
  return useMutation({
    mutationFn: (body: { planCode: string; provider: string }) =>
      api<CreatePaymentResponse>('/payments', { method: 'POST', body }),
  });
}

export function fetchPaymentStatus(id: string, signal?: AbortSignal) {
  return api<PaymentStatusResponse>(`/payments/${id}`, signal ? { signal } : {});
}
